import React from "react";
import { Card, Statistic } from "antd";
import dayjs, { Dayjs } from "dayjs";
import type { PharmacyHeader } from "../../utils/reportExport";

export type DateRange = [Dayjs, Dayjs];

export const DEFAULT_RANGE: DateRange = [dayjs().startOf("month"), dayjs()];

export const RANGE_PRESETS: Array<{ label: string; value: DateRange }> = [
  { label: "Today", value: [dayjs(), dayjs()] },
  {
    label: "Yesterday",
    value: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
  },
  { label: "Last 7 days", value: [dayjs().subtract(6, "day"), dayjs()] },
  { label: "This month", value: [dayjs().startOf("month"), dayjs()] },
  {
    label: "Last month",
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: "This FY",
    value: [
      dayjs().month() >= 3
        ? dayjs().month(3).startOf("month")
        : dayjs().subtract(1, "year").month(3).startOf("month"),
      dayjs(),
    ],
  },
];

export const rangeParams = (range: DateRange) => ({
  startDate: range[0].format("YYYY-MM-DD"),
  endDate: range[1].format("YYYY-MM-DD"),
});

export const rangeLabel = (range: DateRange) =>
  `${range[0].format("DD/MM/YYYY")} - ${range[1].format("DD/MM/YYYY")}`;

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  card: "Card",
  upi: "UPI",
  net_banking: "Net banking",
  insurance: "Insurance",
};

export const REFUND_METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  card: "Card",
  upi: "UPI",
  net_banking: "Net banking",
  adjustment: "Adjustment",
};

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  paid: "Paid",
  pending: "Pending",
  partially_paid: "Partially paid",
  refunded: "Refunded",
};

export const CATEGORY_OPTIONS = [
  "tablet",
  "capsule",
  "syrup",
  "injection",
  "cream",
  "drops",
  "inhaler",
  "other",
].map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));

export const buildPharmacyHeader = (settings: any): PharmacyHeader => {
  const addr = settings?.address || {};
  const addressLine = [addr.street, addr.city, addr.state, addr.zipCode]
    .filter(Boolean)
    .join(", ");
  const rawName = (settings?.pharmacyName || "").trim();
  const name = rawName || "Pharmacy";
  return {
    name,
    addressLine,
    phone: settings?.phone || "",
    email: settings?.email || "",
    gstin: settings?.gstin || "",
    drugLicenseNumber: settings?.drugLicenseNumber || "",
  };
};

export interface StatItem {
  title: string;
  value: number | string;
  prefix?: string;
  precision?: number;
  suffix?: string;
}

export const StatStrip: React.FC<{ stats: StatItem[]; loading: boolean }> = ({
  stats,
  loading,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(150px, 1fr))`,
      gap: 8,
      marginBottom: 12,
    }}
  >
    {stats.map((s) => (
      <Card key={s.title} size="small" loading={loading} styles={{ body: { padding: "8px 12px" } }}>
        <Statistic
          title={<span style={{ fontSize: 12 }}>{s.title}</span>}
          value={s.value}
          prefix={s.prefix}
          suffix={s.suffix}
          precision={s.precision}
          valueStyle={{ fontSize: 18 }}
        />
      </Card>
    ))}
  </div>
);
